import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = process.env.REACT_APP_API_URL || "";

function StatBox({ value, label }) {
  return (
    <div className="stat-box">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

function PlayerTable({ players, winnerTeam, teamNum, teamName }) {
  const teamPlayers = players.filter((p) => p.team === teamNum);
  const isWinner = winnerTeam === teamNum;
  return (
    <div className="card">
      <h2 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        {teamNum === 1 ? "🟦" : "🟥"} {teamName}
        {isWinner && (
          <span style={{ fontSize: "0.75rem", background: "#166534", color: "#4ade80", padding: "0.2rem 0.6rem", borderRadius: "4px" }}>
            WINNER
          </span>
        )}
      </h2>
      <div style={{ overflowX: "auto" }}>
      <table className="table">
        <thead>
          <tr>
            <th>Player</th>
            <th>K</th><th>D</th><th>A</th>
            <th>HS%</th><th>ADR</th><th>KAST</th>
            <th title="Double kills">2K</th>
            <th title="Triple kills">3K</th>
            <th title="Quad kills">4K</th>
            <th title="Ace">5K</th>
            <th title="First kills">FK</th>
            <th title="Clutches won/total">Clutch</th>
            <th title="Enemies flashed">EF</th>
            <th title="Flash assists">FA</th>
            <th>Rating</th>
          </tr>
        </thead>
        <tbody>
          {teamPlayers
            .sort((a, b) => b.hltv_rating - a.hltv_rating)
            .map((p) => (
              <tr key={p.steam_id}>
                <td><Link to={`/players/${p.steam_id}`}>{p.name}</Link></td>
                <td>{p.kills}</td><td>{p.deaths}</td><td>{p.assists}</td>
                <td>{p.kills > 0 ? Math.round((p.headshots / p.kills) * 100) + "%" : "0%"}</td>
                <td>{p.adr}</td>
                <td>{p.kast}%</td>
                <td style={{ color: p.kills_2k > 0 ? "#60a5fa" : "inherit" }}>{p.kills_2k || 0}</td>
                <td style={{ color: p.kills_3k > 0 ? "#34d399" : "inherit" }}>{p.kills_3k || 0}</td>
                <td style={{ color: p.kills_4k > 0 ? "#f59e0b" : "inherit" }}>{p.kills_4k || 0}</td>
                <td style={{ color: p.kills_5k > 0 ? "#ef4444" : "inherit", fontWeight: p.kills_5k > 0 ? "700" : "400" }}>{p.kills_5k || 0}</td>
                <td style={{ color: p.first_kills > 0 ? "#a78bfa" : "inherit" }}>{p.first_kills || 0}</td>
                <td style={{ color: p.clutches_won > 0 ? "#f0a500" : "inherit" }}>
                  {p.clutches_won || 0}{p.clutches_total > 0 ? `/${p.clutches_total}` : ""}
                </td>
                <td>{p.enemies_flashed || 0}</td>
                <td style={{ color: "#9ca3af" }}>{p.flash_assists || 0}</td>
                <td style={{ color: "#f0a500", fontWeight: "600" }}>{p.hltv_rating}</td>
              </tr>
            ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function RoundChart({ rounds }) {
  if (!rounds || rounds.length === 0) return null;
  const data = rounds.map((r) => ({
    name: `R${r.round_number}`,
    winner: r.winner_team,
  }));
  return (
    <div className="card">
      <h2>Round History</h2>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "0.5rem" }}>
        {rounds.map((r) => (
          <div
            key={r.round_number}
            title={`Round ${r.round_number} — ${r.win_reason || ""}`}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "4px",
              background: r.winner_team === 1 ? "#1d4ed8" : "#b91c1c",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.65rem",
              color: "#fff",
              fontWeight: "600",
            }}
          >
            {r.round_number}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: "1rem", marginTop: "0.75rem", fontSize: "0.8rem", color: "#9ca3af" }}>
        <span><span style={{ color: "#3b82f6" }}>■</span> Team 1</span>
        <span><span style={{ color: "#ef4444" }}>■</span> Team 2</span>
      </div>
    </div>
  );
}

export default function MatchDetail() {
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/matches/${id}`)
      .then((r) => {
        setMatch(r.data.match);
        setPlayers(r.data.players || []);
        setRounds(r.data.rounds || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p style={{ color: "#9ca3af" }}>Loading match...</p>;
  if (!match) return <p style={{ color: "#ef4444" }}>Match not found.</p>;

  const totalRounds = (match.team1_score || 0) + (match.team2_score || 0);

  return (
    <div>
      <div style={{ marginBottom: "1rem" }}>
        <Link to="/matches" style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
          ← Back to Matches
        </Link>
      </div>

      {/* Match Header */}
      <div className="card" style={{ textAlign: "center" }}>
        <div style={{ fontSize: "0.85rem", color: "#9ca3af", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {match.map}
        </div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "2rem" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: "700", fontSize: "1.1rem", color: match.winner === 1 ? "#4ade80" : "#e5e7eb" }}>
              {match.team1_name || "Team 1"}
            </div>
          </div>
          <div style={{ fontSize: "2rem", fontWeight: "800", color: "#f0a500" }}>
            {match.team1_score} – {match.team2_score}
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontWeight: "700", fontSize: "1.1rem", color: match.winner === 2 ? "#4ade80" : "#e5e7eb" }}>
              {match.team2_name || "Team 2"}
            </div>
          </div>
        </div>
        {match.finished_at && (
          <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.5rem" }}>
            {new Date(match.finished_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="stat-grid">
        <StatBox value={totalRounds} label="Total Rounds" />
        <StatBox value={players.length} label="Players" />
        <StatBox
          value={players.length > 0 ? Math.max(...players.map((p) => p.kills)) : 0}
          label="Top Kills"
        />
        <StatBox
          value={
            players.length > 0
              ? Math.max(...players.map((p) => parseFloat(p.hltv_rating) || 0)).toFixed(3)
              : "0.000"
          }
          label="Top Rating"
        />
      </div>

      {/* Round History */}
      <RoundChart rounds={rounds} />

      {/* Team Tables */}
      <div className="team-section">
        <PlayerTable
          players={players}
          winnerTeam={match.winner}
          teamNum={1}
          teamName={match.team1_name || "Team 1"}
        />
        <PlayerTable
          players={players}
          winnerTeam={match.winner}
          teamNum={2}
          teamName={match.team2_name || "Team 2"}
        />
      </div>
    </div>
  );
}
